import { useMemo, useState } from 'react';
import { api, hasPanelToken } from '../services/api';
import './StoreSettings.css';

interface Props {
  customers?: Array<{ id: string; cnpj: string; name: string; tradeName?: string | null; email?: string | null; phone?: string | null }>;
}

function maskCnpj(value: string) {
  const digits = value.replace(/\D/g, '').slice(0, 14);
  if (digits.length <= 11) {
    return digits
      .replace(/^(\d{3})(\d)/, '$1.$2')
      .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4');
  }

  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

function normalizeText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function matchesCustomer(customer: { cnpj: string; name: string; tradeName?: string | null; email?: string | null; phone?: string | null }, query: string): boolean {
  const normalizedQuery = normalizeText(query).trim();
  if (!normalizedQuery) return true;

  const queryDigits = normalizedQuery.replace(/\D/g, '');
  const haystack = normalizeText([
    customer.cnpj,
    customer.name,
    customer.tradeName ?? '',
    customer.email ?? '',
    customer.phone ?? '',
  ].join(' ')).replace(/\D/g, '');

  if (queryDigits && haystack.includes(queryDigits)) return true;

  const textHaystack = normalizeText([
    customer.name,
    customer.tradeName ?? '',
    customer.email ?? '',
    customer.phone ?? '',
  ].join(' '));

  const queryTokens = normalizedQuery.split(/\s+/).filter(Boolean);
  return queryTokens.every(token => textHaystack.includes(token));
}

function customerRelevance(customer: { cnpj: string; name: string; tradeName?: string | null; email?: string | null; phone?: string | null }, query: string): number {
  const normalizedQuery = normalizeText(query).trim();
  if (!normalizedQuery) return 0;

  const normalizedName = normalizeText(customer.name);
  const normalizedTrade = normalizeText(customer.tradeName ?? '');
  const normalizedEmail = normalizeText(customer.email ?? '');
  const normalizedPhone = normalizeText(customer.phone ?? '');
  const text = [normalizedName, normalizedTrade, normalizedEmail, normalizedPhone, normalizeText(customer.cnpj)].join(' ');

  const exactName = normalizedName === normalizedQuery ? 130 : normalizedName.startsWith(normalizedQuery) ? 110 : normalizedName.includes(normalizedQuery) ? 70 : 0;
  const exactTrade = normalizedTrade === normalizedQuery ? 95 : normalizedTrade.startsWith(normalizedQuery) ? 85 : normalizedTrade.includes(normalizedQuery) ? 55 : 0;
  const inName = text.includes(normalizedQuery) ? 35 : 0;
  const digits = normalizedQuery.replace(/\D/g, '');
  const cnpjDigits = customer.cnpj.replace(/\D/g, '');
  const phoneDigits = normalizedPhone.replace(/\D/g, '');
  const inDigits = digits ? (cnpjDigits === digits ? 100 : cnpjDigits.startsWith(digits) ? 88 : cnpjDigits.includes(digits) ? 60 : 0) : 0;
  const inPhoneDigits = digits ? (phoneDigits === digits ? 42 : phoneDigits.startsWith(digits) ? 34 : phoneDigits.includes(digits) ? 18 : 0) : 0;

  const queryTokens = normalizedQuery.split(/\s+/).filter(Boolean);
  const tokenMatch = queryTokens.length > 1 && queryTokens.every(token => text.includes(token)) ? 20 : 0;

  if (!digits && !text.includes(normalizedQuery)) {
    return 0;
  }

  return exactName + exactTrade + inName + inDigits + inPhoneDigits + tokenMatch;
}

export default function CustomerAccess({ customers = [] }: Props) {
  const [savingAccess, setSavingAccess] = useState(false);
  const [accessError, setAccessError] = useState('');
  const [accessSuccess, setAccessSuccess] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [customerForm, setCustomerForm] = useState({
    cnpj: '',
    name: '',
    tradeName: '',
    email: '',
    phone: '',
    password: '',
  });

  const filteredCustomers = useMemo(() => {
    const term = customerSearch.trim();
    if (!term) return customers;
    return customers
      .filter(customer => matchesCustomer(customer, term))
      .sort((a, b) => {
        const scoreDiff = customerRelevance(b, term) - customerRelevance(a, term);
        if (scoreDiff !== 0) return scoreDiff;
        return a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' });
      });
  }, [customers, customerSearch]);

  function generatePassword(length = 10): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789@#$%';
    const random = new Uint32Array(length);
    crypto.getRandomValues(random);
    return Array.from(random, n => chars[n % chars.length]).join('');
  }

  function handleCustomerSelect(customerId: string) {
    const selected = customers.find(customer => customer.id === customerId);
    if (!selected) return;

    setSelectedCustomerId(customerId);

    setCustomerForm({
      cnpj: selected.cnpj,
      name: selected.name,
      tradeName: selected.tradeName || '',
      email: selected.email || '',
      phone: selected.phone || '',
      password: '',
    });
  }

  async function handleCustomerAccessSubmit(e: React.FormEvent) {
    e.preventDefault();
    setAccessError('');
    setAccessSuccess('');

    if (!hasPanelToken()) {
      setAccessError('Sessao do painel ausente. Entre novamente para salvar o acesso do cliente.');
      return;
    }

    setSavingAccess(true);

    try {
      const res = await api.saveCustomerAccess({
        cnpj: customerForm.cnpj.trim(),
        name: customerForm.name.trim(),
        password: customerForm.password.trim(),
        tradeName: customerForm.tradeName.trim() || undefined,
        email: customerForm.email.trim() || undefined,
        phone: customerForm.phone.trim() || undefined,
      });
      setAccessSuccess(res.message);
      setCustomerForm(prev => ({ ...prev, password: '' }));
    } catch (err) {
      setAccessError(err instanceof Error ? err.message : 'Erro ao salvar acesso');
    } finally {
      setSavingAccess(false);
    }
  }

  return (
    <section className="store-settings">
      <div className="store-settings-header">
        <h2>Acesso do Cliente</h2>
        <p>Localize o cliente enquanto digita e preencha o cadastro de acesso em uma unica tela.</p>
      </div>

      <form className="store-settings-form" onSubmit={handleCustomerAccessSubmit}>
        <section className="customer-access-search-card">
          <div className="customer-access-search-head">
            <div>
              <h3>Buscar cliente</h3>
              <p>Digite nome, fantasia, CPF/CNPJ ou telefone. Os resultados aparecem na hora.</p>
            </div>
            <span className="customer-access-search-count">{filteredCustomers.length} encontrado{filteredCustomers.length === 1 ? '' : 's'}</span>
          </div>

              <div className="customer-search-wrap">
              <input
                value={customerSearch}
                onChange={e => {
                  const value = e.target.value;
                  setCustomerSearch(value);
                  setSelectedCustomerId('');
                }}
                placeholder="Ex: ISMAEL, 00.000.000/0000-00, nome fantasia..."
                autoComplete="off"
              />

            {customerSearch.trim() && filteredCustomers.length > 0 && (
              <div className="customer-search-suggestions" role="listbox" aria-label="Sugestoes de clientes">
                {filteredCustomers.map(customer => (
                  <button
                    key={customer.id}
                    type="button"
                    className="customer-search-suggestion"
                    onClick={() => {
                      setCustomerSearch(customer.name);
                      handleCustomerSelect(customer.id);
                    }}
                  >
                    <strong>{customer.name}</strong>
                    <span>{customer.tradeName || customer.email || customer.phone || customer.cnpj}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

        </section>

        <section className="customer-access-form-card">
          <div className="customer-access-form-head">
            <div>
              <h3>Cadastro de acesso</h3>
              <p>Atualize os dados e a senha do cliente selecionado.</p>
            </div>
            <select
              className="customer-access-select"
              value={selectedCustomerId}
              onChange={e => handleCustomerSelect(e.target.value)}
            >
              <option value="">Selecionar cliente</option>
              {filteredCustomers.map(customer => (
                <option key={customer.id} value={customer.id}>
                  {customer.name} - {customer.cnpj}
                </option>
              ))}
            </select>
          </div>

          <div className="customer-access-grid">
            <label>
              CPF ou CNPJ do cliente
              <input
                value={customerForm.cnpj}
                onChange={e => setCustomerForm({ ...customerForm, cnpj: maskCnpj(e.target.value) })}
                placeholder="000.000.000-00 ou 00.000.000/0000-00"
              />
            </label>

            <label>
              Nome do cliente
              <input
                value={customerForm.name}
                onChange={e => setCustomerForm({ ...customerForm, name: e.target.value })}
                placeholder="Razão social ou nome"
              />
            </label>

            <label>
              Nome fantasia
              <input
                value={customerForm.tradeName}
                onChange={e => setCustomerForm({ ...customerForm, tradeName: e.target.value })}
                placeholder="Opcional"
              />
            </label>

            <label>
              Email
              <input
                value={customerForm.email}
                onChange={e => setCustomerForm({ ...customerForm, email: e.target.value })}
                placeholder="Opcional"
              />
            </label>

            <label>
              Telefone
              <input
                value={customerForm.phone}
                onChange={e => setCustomerForm({ ...customerForm, phone: e.target.value })}
                placeholder="Opcional"
              />
            </label>

            <label className="customer-access-password">
              Senha de acesso
              <div className="store-settings-password-row">
                <input
                  type="password"
                  value={customerForm.password}
                  onChange={e => setCustomerForm({ ...customerForm, password: e.target.value })}
                  placeholder="Minimo 6 caracteres"
                />
                <button
                  type="button"
                  className="store-settings-generate"
                  onClick={() => setCustomerForm({ ...customerForm, password: generatePassword() })}
                >
                  Gerar senha
                </button>
              </div>
            </label>
          </div>
        </section>

        {accessError && <p className="store-settings-error">{accessError}</p>}
        {accessSuccess && <p className="store-settings-success">{accessSuccess}</p>}

        <button className="store-settings-save" type="submit" disabled={savingAccess}>
          {savingAccess ? 'Salvando...' : 'Salvar acesso do cliente'}
        </button>
      </form>
    </section>
  );
}
